import { Recipe, ShoppingList, User, Invitation } from "../types";
import { db, isFirebaseReady, auth } from "./firebase";
import { 
    collection, 
    doc, 
    setDoc, 
    getDoc,
    addDoc,
    onSnapshot, 
    query, 
    where, 
    updateDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
    getDocs,
    FirestoreError
} from "firebase/firestore";

const GUEST_DATA_KEY = 'supermarct-guest-data';
const GUEST_IMAGES_KEY = 'supermarct-guest-images';
const BACKUP_DATA_KEY = 'supermarct-backup-data';

interface UserData {
    lists: ShoppingList[];
    recipes: Recipe[];
}

const sanitizeFirestoreData = (data: any): any => {
    return JSON.parse(JSON.stringify(data));
};

const createDefaultGuestList = (): ShoppingList => ({
    id: `local-${Date.now()}`,
    name: 'Boodschappen',
    items: [],
    sharedWith: [],
    accessIds: []
});

export const getUserDataKey = (userId?: string | null) => {
    return userId ? `supermarct-user-data-${userId}` : GUEST_DATA_KEY;
};

export const loadUserData = (userId?: string | null): UserData => {
    const key = getUserDataKey(userId);
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && Array.isArray(parsed.lists)) {
                return parsed;
            }
        }
    } catch (e) {}

    // Fallback: If user signs in for first time, initialize with guest/backup data so no data is lost
    if (userId) {
        const guest = loadGuestData();
        const backup = loadBackupData();
        const baseLists = (guest.lists && guest.lists.length > 0) ? guest.lists : (backup?.lists && backup.lists.length > 0 ? backup.lists : [createDefaultGuestList()]);
        const baseRecipes = (guest.recipes && guest.recipes.length > 0) ? guest.recipes : (backup?.recipes || []);

        const initialLists: ShoppingList[] = baseLists.map(l => ({
            ...l,
            ownerId: userId,
            accessIds: Array.from(new Set([...(l.accessIds || []), userId]))
        }));

        const newUserData: UserData = { lists: initialLists, recipes: baseRecipes };
        saveUserData(userId, newUserData);
        return newUserData;
    }

    return { lists: [createDefaultGuestList()], recipes: [] };
};

export const saveUserData = (userId: string | null | undefined, data: UserData) => {
    const key = getUserDataKey(userId);
    try {
        localStorage.setItem(key, JSON.stringify(data));
        saveBackupData(data.lists, data.recipes);
        // Trigger storage event for same-window / multi-tab synchronization
        window.dispatchEvent(new CustomEvent('supermarct-sync', { detail: { userId, data } }));
    } catch (e) {
        console.error("Failed to save user data", e);
    }
};

const loadGuestData = (): UserData => {
    try {
        const raw = localStorage.getItem(GUEST_DATA_KEY);
        if (raw) return JSON.parse(raw);
    } catch (e) {}
    return { lists: [createDefaultGuestList()], recipes: [] };
};

const saveGuestData = (data: UserData) => {
    localStorage.setItem(GUEST_DATA_KEY, JSON.stringify(data));
};

// --- BACKUP LOGIC ---
const loadBackupData = (): UserData | null => {
    try {
        const raw = localStorage.getItem(BACKUP_DATA_KEY);
        if (raw) return JSON.parse(raw);
    } catch (e) {
        console.error("Failed to load backup", e);
    }
    return null;
};

const saveBackupData = (lists: ShoppingList[], recipes: Recipe[]) => {
    try {
        localStorage.setItem(BACKUP_DATA_KEY, JSON.stringify({ lists, recipes }));
    } catch (e) {
        console.error("Failed to save backup", e);
    }
};

// Helper to update a single list in backup
const updateListInBackup = (list: ShoppingList) => {
    const data = loadBackupData() || { lists: [], recipes: [] };
    const idx = data.lists.findIndex(l => l.id === list.id);
    if (idx >= 0) data.lists[idx] = list; else data.lists.push(list);
    saveBackupData(data.lists, data.recipes);
};

// Helper to remove a list from backup
const removeListFromBackup = (listId: string) => {
    const data = loadBackupData();
    if (!data) return;
    data.lists = data.lists.filter(l => l.id !== listId);
    saveBackupData(data.lists, data.recipes);
};

// Helper to update a recipe in backup
const updateRecipeInBackup = (recipe: Recipe) => {
    const data = loadBackupData() || { lists: [], recipes: [] };
    const idx = data.recipes.findIndex(r => r.id === recipe.id);
    if (idx >= 0) data.recipes[idx] = recipe; else data.recipes.push(recipe);
    saveBackupData(data.lists, data.recipes);
};

// Helper to remove a recipe from backup
const removeRecipeFromBackup = (recipeId: string) => {
    const data = loadBackupData();
    if (!data) return;
    data.recipes = data.recipes.filter(r => r.id !== recipeId);
    saveBackupData(data.lists, data.recipes);
};

// --- FEEDBACK LOGIC ---

export const sendFeedbackToAdmin = async (user: User | null, subject: string, message: string, type: string) => {
    const feedbackItem = {
        userId: user?.id || 'guest',
        userName: user?.name || 'Gast',
        userEmail: user?.email || 'geen-email',
        subject,
        message,
        type,
        createdAt: Date.now(),
        status: 'new',
        appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.0.0'
    };

    if (isFirebaseReady() && db) {
        try {
            await addDoc(collection(db, "feedback"), feedbackItem);
            return;
        } catch (e) {
            console.warn("Firebase feedback send failed, saving locally:", e);
        }
    }

    // Offline / Local fallback: store in pending feedback
    try {
        const stored = localStorage.getItem('supermarct-local-feedback');
        const list = stored ? JSON.parse(stored) : [];
        list.push(feedbackItem);
        localStorage.setItem('supermarct-local-feedback', JSON.stringify(list));
    } catch (e) {
        console.error("Local feedback storage error", e);
    }
};

// --- IMAGE DOCUMENT LOGIC (GUEST & CLOUD) ---

export const saveImageDocument = async (user: User | null, base64Data: string): Promise<string> => {
    const imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    
    if (!user || !isFirebaseReady() || !db) {
        // Guest: Save to separate localStorage key to simulate document store
        try {
            const rawImages = localStorage.getItem(GUEST_IMAGES_KEY);
            const images = rawImages ? JSON.parse(rawImages) : {};
            images[imageId] = base64Data;
            localStorage.setItem(GUEST_IMAGES_KEY, JSON.stringify(images));
            return imageId;
        } catch (e) {
            console.error("Local storage image error (quota exceeded?)", e);
            throw new Error("Opslag vol");
        }
    }

    // Cloud: Save to "recipe_images" collection inside user doc or root
    // Using root collection "recipe_images" with security rules allowing owner access is often easier,
    // but nesting under user keeps it organized. Let's use user subcollection.
    const imgRef = doc(db, "users", user.id, "recipe_images", imageId);
    await setDoc(imgRef, {
        data: base64Data,
        createdAt: Date.now()
    });
    
    return imageId;
};

export const getImageDocument = async (user: User | null, imageId: string): Promise<string | null> => {
    if (!user || !isFirebaseReady() || !db) {
        const rawImages = localStorage.getItem(GUEST_IMAGES_KEY);
        if (!rawImages) return null;
        const images = JSON.parse(rawImages);
        return images[imageId] || null;
    }

    try {
        const imgRef = doc(db, "users", user.id, "recipe_images", imageId);
        const snap = await getDoc(imgRef);
        if (snap.exists()) {
            return snap.data().data;
        }
        return null;
    } catch (e) {
        console.error("Error fetching image doc", e);
        return null;
    }
};

export const subscribeToData = (user: User | null, onDataUpdate: (data: UserData) => void) => {
    // 1. Deliver user-specific (or guest) data immediately
    const currentData = loadUserData(user?.id);
    onDataUpdate(currentData);

    let activeLists = currentData.lists;
    let activeRecipes = currentData.recipes;

    // 2. Local reactive synchronization listener (custom event & storage events)
    const handleSync = (e: any) => {
        const updatedData = loadUserData(user?.id);
        onDataUpdate(updatedData);
    };

    window.addEventListener('supermarct-sync', handleSync);
    window.addEventListener('storage', handleSync);

    // 3. Cloud synchronization if Firebase is online
    let unsubLists = () => {};
    let unsubRecipes = () => {};
    let cancelled = false;

    const startCloudSync = () => {
        if (cancelled || !user || !canSyncToCloud(user)) return;

        const qLists = query(collection(db, "shoppingLists"), where("accessIds", "array-contains", user.id));
        unsubLists = onSnapshot(qLists, (snapshot) => {
            const cloudLists = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ShoppingList));
            const cloudIds = new Set(cloudLists.map(l => l.id));
            const syncedBefore = getSyncedIds('lists', user.id);

            // Lokale lijsten die nog nooit in de cloud stonden: bewaren en uploaden
            const localOnly = activeLists.filter(l => !cloudIds.has(l.id) && !syncedBefore.has(l.id) && !snapshot.metadata.fromCache);
            localOnly.forEach(l => saveListToCloud(user, l));
            markSyncedId('lists', user.id, ...Array.from(cloudIds));

            activeLists = [...cloudLists, ...localOnly];
            if (activeLists.length === 0) return;
            const fresh = { lists: activeLists, recipes: activeRecipes };
            saveUserData(user.id, fresh);
            onDataUpdate(fresh);
        }, (error) => {
            console.warn("Firestore lists sync:", error);
        });

        const recipesColRef = collection(db, "users", user.id, "recipes");
        unsubRecipes = onSnapshot(recipesColRef, (snapshot) => {
            const cloudRecipes = snapshot.docs.map(d => ({ ...d.data() } as Recipe));
            const cloudIds = new Set(cloudRecipes.map(r => r.id));
            const syncedBefore = getSyncedIds('recipes', user.id);

            const localOnly = activeRecipes.filter(r => !cloudIds.has(r.id) && !syncedBefore.has(r.id) && !snapshot.metadata.fromCache);
            localOnly.forEach(r => saveRecipeToCloud(user, r));
            markSyncedId('recipes', user.id, ...Array.from(cloudIds));

            activeRecipes = [...cloudRecipes, ...localOnly];
            if (activeRecipes.length === 0) return;
            const fresh = { lists: activeLists, recipes: activeRecipes };
            saveUserData(user.id, fresh);
            onDataUpdate(fresh);
        }, (error) => {
            console.warn("Firestore recipes sync:", error);
        });
    };

    if (user && isFirebaseReady() && db && auth) {
        if (auth.currentUser) {
            startCloudSync();
        } else {
            // Bij het opstarten herstelt Firebase de login pas na een moment; daarna pas luisteren
            auth.authStateReady().then(startCloudSync).catch(() => {});
        }
    }

    return () => { 
        cancelled = true;
        window.removeEventListener('supermarct-sync', handleSync);
        window.removeEventListener('storage', handleSync);
        unsubLists(); 
        unsubRecipes(); 
    };
};

// --- LIVE SAMENWERKEN AAN LIJSTEN ---

/** Kan deze gebruiker echt met de cloud synchroniseren (ingelogd bij Firebase met hetzelfde id)? */
export const canSyncToCloud = (user: User | null): boolean =>
    !!user && isFirebaseReady() && !!db && !!auth?.currentUser && auth.currentUser.uid === user.id;

const generateShareToken = () => {
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(36).padStart(2, '0')).join('').slice(0, 24);
};

/**
 * Zorgt dat de lijst een deelsleutel heeft en in Firestore staat, zodat iemand met de link
 * live kan meedoen. Geeft de lijst (met sleutel) direct terug; het opslaan gebeurt op de achtergrond.
 * Zonder cloud-login: null (dan wordt alleen een kopie gedeeld).
 */
export const ensureListShareToken = (user: User | null, list: ShoppingList): ShoppingList | null => {
    if (!canSyncToCloud(user)) return null;
    if (list.shareToken) return list;
    const withToken: ShoppingList = { ...list, shareToken: generateShareToken() };
    saveListToCloud(user, withToken);
    return withToken;
};

/** Voegt de gebruiker met de deelsleutel toe aan een gedeelde lijst (Firestore regels controleren de sleutel). */
export const joinSharedList = async (user: User, listId: string, token: string): Promise<boolean> => {
    if (!canSyncToCloud(user)) return false;
    try {
        await updateDoc(doc(db, "shoppingLists", listId), {
            accessIds: arrayUnion(user.id),
            sharedWith: arrayUnion(sanitizeFirestoreData({
                id: user.id,
                name: user.name,
                email: user.email,
                ...(user.photoUrl ? { photoUrl: user.photoUrl } : {})
            })),
            joinToken: token
        });
        markSyncedId('lists', user.id, listId);
        return true;
    } catch (error) {
        console.error("Deelnemen aan lijst mislukt:", error);
        return false;
    }
};

// Onthoud welke lijsten/recepten ooit in de cloud stonden. Staat iets lokaal maar nooit in de
// cloud, dan uploaden we het; stond het er wel en is het weg, dan is het elders verwijderd.
const syncedIdsKey = (kind: 'lists' | 'recipes', userId: string) => `supermarct-synced-${kind}-${userId}`;

const getSyncedIds = (kind: 'lists' | 'recipes', userId: string): Set<string> => {
    try { return new Set(JSON.parse(localStorage.getItem(syncedIdsKey(kind, userId)) || '[]')); } catch { return new Set(); }
};

const markSyncedId = (kind: 'lists' | 'recipes', userId: string, ...ids: string[]) => {
    try {
        const set = getSyncedIds(kind, userId);
        ids.forEach(id => set.add(id));
        localStorage.setItem(syncedIdsKey(kind, userId), JSON.stringify(Array.from(set)));
    } catch { /* geen probleem */ }
};

export const saveListToCloud = async (user: User | null, list: ShoppingList) => {
    // 1. Always save to user-specific local storage immediately for speed & resilience
    const data = loadUserData(user?.id);
    const idx = data.lists.findIndex(l => l.id === list.id);
    if (idx >= 0) data.lists[idx] = list; else data.lists.push(list);
    saveUserData(user?.id, data);

    // 2. Sync to cloud if Firebase is available
    if (user && isFirebaseReady() && db) {
        let ownerId = list.ownerId;
        if (!ownerId || ownerId === 'guest') {
            ownerId = user.id;
        }

        // Alleen jezelf (en de eigenaar) toevoegen; andere leden worden nooit vanaf dit toestel gezet,
        // zodat een verwijderd lid niet terugkomt via een verouderde lokale kopie.
        const accessIds = new Set<string>([user.id]);
        if (ownerId && ownerId !== 'guest') accessIds.add(ownerId);

        // sharedWith/accessIds worden beheerd door deelnemen/verwijderen. Nooit overschrijven met
        // de (mogelijk verouderde) lokale versie, anders verdwijnt een net toegevoegd lid weer.
        const { sharedWith, accessIds: _localAccessIds, ...rest } = list;

        try {
            await setDoc(doc(db, "shoppingLists", list.id), {
                ...sanitizeFirestoreData({
                    ...rest,
                    lastModified: Date.now(),
                    ownerId: ownerId,
                    lastModifiedBy: user.name || user.email || user.id,
                    lastModifiedById: user.id
                }),
                accessIds: arrayUnion(...Array.from(accessIds))
            }, { merge: true });
            markSyncedId('lists', user.id, list.id);
        } catch (error: any) {
            console.warn("Firestore sync fallback to local storage:", error);
        }
    }
};

export const deleteListFromCloud = async (user: User | null, listId: string) => {
    // 1. Remove from user data
    const data = loadUserData(user?.id);
    data.lists = data.lists.filter(l => l.id !== listId);
    saveUserData(user?.id, data);

    // 2. Remove from cloud if available
    if (user && isFirebaseReady() && db) {
        try {
            await deleteDoc(doc(db, "shoppingLists", listId));
        } catch (error: any) {
            console.warn("Firestore delete fallback to local storage:", error);
        }
    }
};

export const removeUserFromList = async (listId: string, userIdToRemove: string) => {
    if (!isFirebaseReady() || !db) return;
    
    try {
        const listRef = doc(db, "shoppingLists", listId);
        const listSnap = await getDoc(listRef);
        
        if (listSnap.exists()) {
            const listData = listSnap.data() as ShoppingList;
            // Filter user from sharedWith array
            const updatedSharedWith = listData.sharedWith.filter(u => u.id !== userIdToRemove);
            
            await updateDoc(listRef, {
                sharedWith: updatedSharedWith,
                accessIds: arrayRemove(userIdToRemove)
            });
        }
    } catch (error) {
        console.error("Remove user error:", error);
    }
};

// --- INVITATION LOGIC ---

export const sendInvitation = async (user: User, listId: string, listName: string, receiverEmail: string) => {
    if (!isFirebaseReady() || !db) throw new Error("Offline");
    
    try {
        await addDoc(collection(db, "invitations"), {
            listId,
            listName,
            senderId: user.id,
            senderName: user.name,
            receiverEmail: receiverEmail.toLowerCase().trim(),
            status: 'pending',
            createdAt: Date.now()
        });
    } catch (error: any) {
        console.error("Send invitation error:", error);
        throw new Error(`Uitnodiging mislukt: ${error.message}`);
    }
};

export const cancelInvitation = async (invitationId: string) => {
    if (!isFirebaseReady() || !db) return;
    try {
        await deleteDoc(doc(db, "invitations", invitationId));
    } catch (error) {
        console.error("Cancel invitation error:", error);
    }
};

export const subscribeToInvitations = (userEmail: string, callback: (invites: Invitation[]) => void) => {
    const isAuthReady = auth && auth.currentUser;
    if (!isFirebaseReady() || !db || !isAuthReady) return () => {};
    
    const q = query(
        collection(db, "invitations"), 
        where("receiverEmail", "==", userEmail.toLowerCase().trim()),
        where("status", "==", "pending")
    );

    return onSnapshot(q, (snapshot) => {
        const invites = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invitation));
        callback(invites);
    }, (error) => {
        console.error("Error subscribing to invitations:", error);
    });
};

export const respondToInvitation = async (user: User, invitation: Invitation, accept: boolean) => {
    if (!isFirebaseReady() || !db) return;

    try {
        const batch =  (await import("firebase/firestore")).writeBatch(db);
        
        // 1. Update Invitation status
        const inviteRef = doc(db, "invitations", invitation.id);
        batch.update(inviteRef, { status: accept ? 'accepted' : 'declined' });

        if (accept) {
            // 2. Add user to ShoppingList
            const listRef = doc(db, "shoppingLists", invitation.listId);
            batch.update(listRef, {
                sharedWith: arrayUnion(user), // Sla basis user info op voor weergave
                accessIds: arrayUnion(user.id) // Voor permissies/queries
            });
        }

        await batch.commit();
    } catch (error) {
        console.error("Respond invitation error:", error);
    }
};

export const getPendingInvitationsForList = async (listId: string): Promise<Invitation[]> => {
    if (!isFirebaseReady() || !db) return [];
    try {
        const q = query(collection(db, "invitations"), where("listId", "==", listId), where("status", "==", "pending"));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({id: d.id, ...d.data()} as Invitation));
    } catch (error) {
        console.error("Get pending invites error:", error);
        return [];
    }
};

// --- RECIPE LOGIC ---

export const saveRecipeToCloud = async (user: User | null, recipe: Recipe) => {
    // 1. Save to user data
    const data = loadUserData(user?.id);
    const idx = data.recipes.findIndex(r => r.id === recipe.id);
    if (idx >= 0) data.recipes[idx] = recipe; else data.recipes.push(recipe);
    saveUserData(user?.id, data);

    // 2. Sync to cloud if Firebase is available
    if (user && isFirebaseReady() && db) {
        try {
            const recipeDocRef = doc(db, "users", user.id, "recipes", recipe.id);
            await setDoc(recipeDocRef, sanitizeFirestoreData({ ...recipe, lastModified: Date.now() }), { merge: true });
            markSyncedId('recipes', user.id, recipe.id);
        } catch (error: any) {
            console.warn("Firestore recipe save fallback to local storage:", error);
        }
    }
};

export const deleteRecipeFromCloud = async (user: User | null, recipeId: string) => {
    // 1. Remove from user data
    const data = loadUserData(user?.id);
    data.recipes = data.recipes.filter(r => r.id !== recipeId);
    saveUserData(user?.id, data);

    // 2. Remove from cloud if available
    if (user && isFirebaseReady() && db) {
        try {
            const recipeDocRef = doc(db, "users", user.id, "recipes", recipeId);
            await deleteDoc(recipeDocRef);
        } catch (error: any) {
            console.warn("Firestore delete recipe fallback to local storage:", error);
        }
    }
};

// Verkleint een (base64) receptfoto zodat hij in een Firestore document (max 1 MiB) past.
const shrinkImageForShare = (src: string, maxWidth = 800, maxChars = 400 * 1024): Promise<string | null> => {
    return new Promise((resolve) => {
        if (!src || !src.startsWith('data:image')) { resolve(null); return; }
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(null); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            let quality = 0.75;
            let out = canvas.toDataURL('image/jpeg', quality);
            while (out.length > maxChars && quality > 0.3) {
                quality -= 0.1;
                out = canvas.toDataURL('image/jpeg', quality);
            }
            resolve(out.length <= maxChars ? out : null);
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
};

// Schrijft het recept (met verkleinde foto) op de achtergrond naar Firestore onder een vooraf gekozen id.
const publishRecipeInBackground = (shareId: string, user: User | null, recipe: Recipe) => {
    (async () => {
        try {
            let image = recipe.image;
            if (image?.startsWith('data:')) {
                image = (await shrinkImageForShare(image)) || '';
            }
            const { stepImageIds, ...rest } = recipe;
            await setDoc(doc(db, "public_recipes", shareId), sanitizeFirestoreData({
                ...rest,
                image,
                originalAuthor: user ? user.name : "Gastgebruiker",
                sharedAt: Date.now()
            }));
        } catch (e) {
            console.warn("Recept publiceren in Firestore mislukt (link werkt nog steeds, zonder foto):", e);
        }
    })();
};

// --- RECIPE SHARE LINKS ---

const toBase64Url = (str: string) =>
    btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const fromBase64Url = (b64: string) => {
    let s = b64.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return decodeURIComponent(escape(atob(s)));
};

export const encodeSharedRecipeData = (recipe: Recipe, authorName: string): string => {
    const payload = {
        t: recipe.title,
        // Base64 foto's zijn te groot voor een link; alleen externe URL's meesturen
        i: recipe.image && /^https?:\/\//.test(recipe.image) ? recipe.image : '',
        tm: recipe.time,
        c: recipe.category,
        g: recipe.ingredients.map(ing => [ing.name, ing.amount]),
        s: recipe.steps,
        a: authorName
    };
    return toBase64Url(JSON.stringify(payload));
};

export const decodeSharedRecipeData = (encoded: string): Recipe | null => {
    try {
        const p = JSON.parse(fromBase64Url(encoded));
        if (!p || !p.t) return null;
        return {
            id: `shared-${Date.now()}`,
            title: p.t,
            image: p.i || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80',
            time: p.tm || '',
            category: p.c || 'Diner',
            ingredients: (p.g || []).map((g: [string, string]) => ({ name: g[0], amount: g[1] })),
            steps: p.s || [],
            isFavorite: false,
            originalAuthor: p.a,
            status: 'ready'
        };
    } catch (e) {
        console.error("Failed to decode shared recipe data", e);
        return null;
    }
};

/**
 * Maakt altijd een werkende deellink voor een recept.
 * Eerst via Firestore (korte link). Lukt dat niet (geen Firebase login, offline, ...),
 * dan zit het recept zelf in de link.
 */
/**
 * Maakt direct (zonder wachten) een deellink voor een recept.
 * Het recept zelf zit in de link, dus hij werkt altijd. Ben je ingelogd bij Firebase, dan wordt
 * daarnaast op de achtergrond een kopie mét foto opgeslagen; de link verwijst daar ook naar (share_id).
 */
export const createRecipeShareLink = (user: User | null, recipe: Recipe): string => {
    const params = new URLSearchParams();
    if (isFirebaseReady() && db && auth?.currentUser) {
        const shareId = doc(collection(db, "public_recipes")).id;
        publishRecipeInBackground(shareId, user, recipe);
        params.set('share_id', shareId);
    }
    params.set('share_recipe_data', encodeSharedRecipeData(recipe, user?.name || 'Een superMarct gebruiker'));
    return `${window.location.origin}/?${params.toString()}`;
};

/**
 * Een "token" voor een gedeeld recept is de querystring van de deellink
 * (share_id en/of share_recipe_data). Oudere tokens: kaal id, of 'data:<encoded>'.
 */
export const parseSharedRecipeToken = (token: string): { shareId: string | null; data: string | null } => {
    if (token.startsWith('data:')) return { shareId: null, data: token.substring(5) };
    if (!token.includes('=')) return { shareId: token, data: null };
    const params = new URLSearchParams(token);
    return { shareId: params.get('share_id'), data: params.get('share_recipe_data') };
};

export const recipeTokenToShareUrl = (token: string) => {
    const { shareId, data } = parseSharedRecipeToken(token);
    const params = new URLSearchParams();
    if (shareId) params.set('share_id', shareId);
    if (data) params.set('share_recipe_data', data);
    return `${window.location.origin}/?${params.toString()}`;
};

/** Snelle versie: receptdata uit de link (direct), anders Firestore. */
export const resolveSharedRecipeToken = async (token: string): Promise<Recipe | null> => {
    const { shareId, data } = parseSharedRecipeToken(token);
    if (data) {
        const decoded = decodeSharedRecipeData(data);
        if (decoded) return decoded;
    }
    return shareId ? fetchPublicRecipe(shareId) : null;
};

export const fetchPublicRecipe = async (shareId: string): Promise<Recipe | null> => {
    if (!isFirebaseReady() || !db) return null;
    try {
        const docSnap = await getDoc(doc(db, "public_recipes", shareId));
        return docSnap.exists() ? { ...docSnap.data() } as Recipe : null;
    } catch (e) { return null; }
};

export const loadInitialData = (userId?: string | null) => loadUserData(userId);

// --- SHARED LIST & WHATSAPP EXPORT LOGIC ---

export interface SharedListData {
    id: string;
    name: string;
    ownerName: string;
    items: { id: string; name: string; quantity?: string; checked: boolean; category?: string }[];
    sharedAt: number;
    /** Deelsleutel: hiermee kan de ontvanger live meedoen aan de lijst (zie joinSharedList) */
    token?: string;
}

export const CURRENT_USER_KEY = 'supermarct-current-user';
export const BACKUP_USER_KEY = 'supermarct-user-session-backup';

export const persistUserSession = (user: User) => {
    if (!user || !user.id) return;
    try {
        const serialized = JSON.stringify(user);
        localStorage.setItem(CURRENT_USER_KEY, serialized);
        localStorage.setItem(BACKUP_USER_KEY, serialized);
        localStorage.setItem('supermarct-onboarding-completed', 'true');
        
        try {
            const encoded = encodeURIComponent(serialized);
            document.cookie = `supermarct_user=${encoded}; path=/; max-age=31536000; SameSite=Lax`;
        } catch (_) {}
    } catch (e) {
        console.error("Failed to persist user session", e);
    }
};

export const getPersistedUserSession = (): User | null => {
    try {
        // 1. Primary localStorage key
        const raw = localStorage.getItem(CURRENT_USER_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.id && parsed.email) return parsed;
        }

        // 2. Backup localStorage key
        const backupRaw = localStorage.getItem(BACKUP_USER_KEY);
        if (backupRaw) {
            const parsed = JSON.parse(backupRaw);
            if (parsed && parsed.id && parsed.email) {
                localStorage.setItem(CURRENT_USER_KEY, backupRaw);
                return parsed;
            }
        }

        // 3. Cookie fallback
        if (typeof document !== 'undefined' && document.cookie) {
            const match = document.cookie.match(/(?:^|; )supermarct_user=([^;]*)/);
            if (match && match[1]) {
                const parsed = JSON.parse(decodeURIComponent(match[1]));
                if (parsed && parsed.id && parsed.email) {
                    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(parsed));
                    return parsed;
                }
            }
        }
    } catch (e) {
        console.warn("Could not retrieve persisted user session", e);
    }
    return null;
};

/** Account uit de oude nep-accountkiezer (geen echte Google/Firebase login). */
export const isLegacyLocalUser = (user: User | null | undefined): boolean =>
    !!user && user.id.startsWith('google-') && !/^google-\d+$/.test(user.id);

/**
 * Zet lokale data van een oud (nep) account over naar het echte account bij de eerste echte login,
 * zodat lijsten en recepten niet verdwijnen.
 */
export const migrateLegacyLocalUser = (newUser: User) => {
    try {
        const previous = getPersistedUserSession();
        if (!previous || previous.id === newUser.id || !isLegacyLocalUser(previous)) return;
        if (previous.email?.toLowerCase().trim() !== newUser.email?.toLowerCase().trim()) return;
        const oldKey = getUserDataKey(previous.id);
        const newKey = getUserDataKey(newUser.id);
        const oldRaw = localStorage.getItem(oldKey);
        if (!oldRaw || localStorage.getItem(newKey)) return;
        const oldData: UserData = JSON.parse(oldRaw);
        const migrated: UserData = {
            recipes: oldData.recipes || [],
            lists: (oldData.lists || []).map(l => ({
                ...l,
                ownerId: l.ownerId === previous.id ? newUser.id : l.ownerId,
                accessIds: Array.from(new Set([...(l.accessIds || []).filter(id => id !== previous.id), newUser.id]))
            }))
        };
        localStorage.setItem(newKey, JSON.stringify(migrated));
    } catch (e) {
        console.warn("Could not migrate legacy local user data", e);
    }
};

export const clearPersistedUserSession = () => {
    try {
        localStorage.removeItem(CURRENT_USER_KEY);
        localStorage.removeItem(BACKUP_USER_KEY);
        if (typeof document !== 'undefined') {
            document.cookie = "supermarct_user=; path=/; max-age=0; SameSite=Lax";
        }
    } catch (e) {
        console.error("Error clearing persisted user session", e);
    }
};

// Deep Linking & PWA Install Detection
export const PENDING_SHARED_LIST_KEY = 'supermarct-pending-shared-list';
export const PENDING_SHARED_RECIPE_KEY = 'supermarct-pending-shared-recipe';

export const isStandaloneApp = (): boolean => {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://')
    );
};

export const savePendingSharedList = (rawEncodedData: string) => {
    try {
        localStorage.setItem(PENDING_SHARED_LIST_KEY, rawEncodedData);
        // Also cookie fallback
        try {
            document.cookie = `supermarct_shared_list=${encodeURIComponent(rawEncodedData)}; path=/; max-age=86400; SameSite=Lax`;
        } catch (_) {}
    } catch (e) {
        console.warn("Could not save pending shared list", e);
    }
};

export const getPendingSharedList = (): string | null => {
    try {
        const raw = localStorage.getItem(PENDING_SHARED_LIST_KEY);
        if (raw) return raw;
        if (typeof document !== 'undefined' && document.cookie) {
            const match = document.cookie.match(/(?:^|; )supermarct_shared_list=([^;]*)/);
            if (match && match[1]) {
                return decodeURIComponent(match[1]);
            }
        }
    } catch (e) {}
    return null;
};

export const clearPendingSharedList = () => {
    try {
        localStorage.removeItem(PENDING_SHARED_LIST_KEY);
        if (typeof document !== 'undefined') {
            document.cookie = "supermarct_shared_list=; path=/; max-age=0; SameSite=Lax";
        }
    } catch (e) {}
};

export const savePendingSharedRecipe = (shareId: string) => {
    try {
        localStorage.setItem(PENDING_SHARED_RECIPE_KEY, shareId);
        try {
            document.cookie = `supermarct_shared_recipe=${encodeURIComponent(shareId)}; path=/; max-age=86400; SameSite=Lax`;
        } catch (_) {}
    } catch (e) {
        console.warn("Could not save pending shared recipe", e);
    }
};

export const getPendingSharedRecipe = (): string | null => {
    try {
        const raw = localStorage.getItem(PENDING_SHARED_RECIPE_KEY);
        if (raw) return raw;
        if (typeof document !== 'undefined' && document.cookie) {
            const match = document.cookie.match(/(?:^|; )supermarct_shared_recipe=([^;]*)/);
            if (match && match[1]) {
                return decodeURIComponent(match[1]);
            }
        }
    } catch (e) {}
    return null;
};

export const clearPendingSharedRecipe = () => {
    try {
        localStorage.removeItem(PENDING_SHARED_RECIPE_KEY);
        if (typeof document !== 'undefined') {
            document.cookie = "supermarct_shared_recipe=; path=/; max-age=0; SameSite=Lax";
        }
    } catch (e) {}
};

export const isIOSDevice = (): boolean =>
    typeof navigator !== 'undefined' &&
    (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) { /* val terug op execCommand */ }
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
    } catch (_) {
        return false;
    }
};

export const buildWhatsAppUrl = (message: string, phone?: string) =>
    phone
        ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`;

/**
 * "Openen in app" vanuit de browser.
 * Een website kan een geïnstalleerde PWA niet zelf openen. Daarom:
 * - Android: de PWA deelt opslag met Chrome, dus het item staat al klaar (pending) en verschijnt
 *   zodra de gebruiker de app opent.
 * - iPhone: de beginscherm-app heeft eigen opslag; we kopiëren de link zodat de gebruiker hem
 *   in de app kan plakken via "Gedeelde link openen".
 */
export const prepareOpenInApp = async (shareUrl: string): Promise<boolean> => {
    return copyTextToClipboard(shareUrl);
};


export const KNOWN_USERS_KEY = 'supermarct-known-users';

export const recordKnownUser = async (user: User) => {
    if (!user || !user.email) return;
    try {
        const raw = localStorage.getItem(KNOWN_USERS_KEY);
        const users: User[] = raw ? JSON.parse(raw) : [];
        const cleanEmail = user.email.toLowerCase().trim();
        const existingIdx = users.findIndex(u => u.email.toLowerCase().trim() === cleanEmail);
        if (existingIdx >= 0) {
            users[existingIdx] = { ...users[existingIdx], ...user };
        } else {
            users.push(user);
        }
        localStorage.setItem(KNOWN_USERS_KEY, JSON.stringify(users));

        if (isFirebaseReady() && db) {
            const userRef = doc(db, "users", user.id);
            await setDoc(userRef, sanitizeFirestoreData({
                id: user.id,
                name: user.name,
                email: cleanEmail,
                photoUrl: user.photoUrl || '',
                lastSeen: Date.now()
            }), { merge: true });
        }
    } catch (e) {
        console.warn("Could not record known user", e);
    }
};

export const checkUserStatus = async (email: string, activeList: ShoppingList): Promise<{
    exists: boolean;
    isAlreadyMember: boolean;
    name?: string;
}> => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
        return { exists: false, isAlreadyMember: false };
    }
    
    // 1. Check if already member of current list
    if (activeList.sharedWith?.some(u => u.email.toLowerCase() === cleanEmail)) {
        return { exists: true, isAlreadyMember: true };
    }

    // 2. Check local storage known users
    try {
        const raw = localStorage.getItem(KNOWN_USERS_KEY);
        if (raw) {
            const knownUsers: User[] = JSON.parse(raw);
            const found = knownUsers.find(u => u.email?.toLowerCase().trim() === cleanEmail);
            if (found) {
                return { exists: true, isAlreadyMember: false, name: found.name };
            }
        }
        
        const currentUser = localStorage.getItem('supermarct-current-user');
        if (currentUser) {
            const parsed = JSON.parse(currentUser);
            if (parsed.email?.toLowerCase().trim() === cleanEmail) {
                return { exists: true, isAlreadyMember: false, name: parsed.name };
            }
        }
    } catch (e) {}

    // 3. Check if Firebase has this user
    if (isFirebaseReady() && db) {
        try {
            const q = query(collection(db, "users"), where("email", "==", cleanEmail));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const userData = snap.docs[0].data();
                return { exists: true, isAlreadyMember: false, name: userData.name };
            }
        } catch (e) {
            console.warn("User lookup check fallback:", e);
        }
    }

    return { exists: false, isAlreadyMember: false };
};

export const encodeSharedListData = (list: ShoppingList, inviterName: string): string => {
    const payload: SharedListData = {
        id: list.id,
        name: list.name,
        ownerName: inviterName || list.ownerName || 'superMarct gebruiker',
        items: (list.items || []).map(i => ({
            id: i.id,
            name: i.name,
            quantity: i.quantity || '',
            checked: i.checked || false,
            category: i.category || 'Overig'
        })),
        sharedAt: Date.now(),
        ...(list.shareToken ? { token: list.shareToken } : {})
    };
    try {
        const jsonStr = JSON.stringify(payload);
        return encodeURIComponent(btoa(unescape(encodeURIComponent(jsonStr))));
    } catch (e) {
        return encodeURIComponent(JSON.stringify(payload));
    }
};

export const decodeSharedListData = (encoded: string): SharedListData | null => {
    try {
        let decodedStr = decodeURIComponent(encoded);
        try {
            decodedStr = decodeURIComponent(escape(atob(decodedStr)));
        } catch (err) {
            // fallback if it wasn't btoa base64 encoded
        }
        const data = JSON.parse(decodedStr);
        if (data && data.name && Array.isArray(data.items)) {
            return data as SharedListData;
        }
    } catch (e) {
        console.error("Failed to decode shared list data", e);
    }
    return null;
};